import { useState, useEffect } from "react";
import PropTypes from "prop-types";

import ConfirmTrackingNumber from "./ConfirmTrackingNumber";
import NumberSelection from "./NumberSelection";
import ConfirmDeleteTrackingNumber from "./ConfirmDeleteTrackingNumber";
import TrackingNumberDetailsModal from "./TrackingNumberDetailsModal";
import { CountryCodes } from "./shared";

const TrackingNumberModal = ({
  isNewNumber,
  trackingNumber,
  accountOwnerId,
  agents,
  admins,
  sources,
  onNumbersChanged,
  onClose,
}) => {
  const [countryCode, setCountryCode] = useState(CountryCodes[0]);
  const [areaCode, setAreaCode] = useState("");
  const [generateTollFree, setGenerateTollFree] = useState(false);
  const [numberOptions, setNumberOptions] = useState([]);
  const [name, setName] = useState(trackingNumber.name || "");
  const [inboundRecording, setInboundRecording] = useState(trackingNumber.inboundRecording || "disabled");
  const [inboundNotificationMessageCondition, setInboundNotificationMessageCondition] = useState(
    trackingNumber.inboundNotificationMessageCondition || "always",
  );

  const [createLead, setCreateLead] = useState(
    trackingNumber.createLead !== undefined ? trackingNumber.createLead : true,
  );
  const [isEnabledInboundTexts, setIsEnabledInboundTexts] = useState(
    Boolean(trackingNumber?.inboundSmsUserId),
  );
  const [chosenNumber, setChosenNumber] = useState({});
  const [source, setSource] = useState(trackingNumber.source ?? "");
  const [selectedUsers, setSelectedUsers] = useState(trackingNumber.users ?? []);
  const [selectedTextInboundUser, setSelectedTextInboundUsers] = useState(
    trackingNumber?.inboundSmsUserId
      ? admins.find((person) => person.id == trackingNumber?.inboundSmsUserId)
      : accountOwnerId
        ? admins.find((person) => person.id == accountOwnerId)
        : null,
  );

  const resolveFallbackUser = () => {
    if (trackingNumber?.fallbackUserId) {
      return admins.find((person) => person.id === trackingNumber.fallbackUserId) || null;
    }
    if (accountOwnerId) {
      return admins.find((person) => person.id === accountOwnerId) || null;
    }
    return null;
  };

  const [noAnswerSetting, setNoAnswerSetting] = useState(trackingNumber.noAnswerSetting || "voicemail");
  const [routingMethod, setRoutingMethod] = useState(trackingNumber.routingMethod || "first_to_claim");
  const [fallback, setFallback] = useState(trackingNumber.fallback || "voicemail");
  const [selectedFallbackUser, setSelectedFallbackUser] = useState(resolveFallbackUser());
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
  const [isNumberConfirmed, setIsNumberConfirmed] = useState(false);

  const confirmationModalShowing = showConfirmationModal || showDeleteConfirmationModal;
  const showNumberSelection = isNewNumber && !isNumberConfirmed && !confirmationModalShowing;
  const showDetailsModal = (isNumberConfirmed || !isNewNumber) && !confirmationModalShowing;

  const [voicemailGreetingChanged, setVoicemailGreetingChanged] = useState(false);
  const [voicemailGreetingFile, setVoicemailGreetingFile] = useState(
    trackingNumber.voicemailGreetingUrl || null,
  );
  const [voicemailGreetingFilename, setVoicemailGreetingFilename] = useState(
    trackingNumber.voicemailGreeting || null,
  );

  useEffect(() => {
    if ((fallback === "voicemail" && routingMethod === "first_to_claim") || routingMethod === "round_robin") {
      setSelectedFallbackUser(null);
    }
    if (fallback !== "voicemail" && routingMethod === "first_to_claim" && selectedFallbackUser === null) {
      setSelectedFallbackUser(resolveFallbackUser());
    }
  }, [fallback, routingMethod]);

  const confirmTrackingNumber = () => {
    setIsNumberConfirmed(true);
  };

  const purchaseNumber = () => {
    setShowConfirmationModal(true);
  };

  const beginDeletionConfirmation = () => {
    setShowDeleteConfirmationModal(true);
  };

  const chooseNotToDelete = () => {
    setShowDeleteConfirmationModal(false);
  };

  const backToNumberSelection = () => {
    setIsNumberConfirmed(false);
  };

  const handleToggle = () => {
    setIsEnabledInboundTexts(!isEnabledInboundTexts);
    setSelectedTextInboundUsers(
      trackingNumber?.inboundSmsUserId
        ? admins.find((person) => person.id == trackingNumber?.inboundSmsUserId)
        : accountOwnerId
          ? admins.find((person) => person.id == accountOwnerId)
          : null,
    );
  };

  return (
    <>
      {showNumberSelection && (
        // show the number selection modal
        <NumberSelection
          chosenNumber={chosenNumber}
          setChosenNumber={setChosenNumber}
          onConfirmNumber={confirmTrackingNumber}
          onClose={onClose}
          countryCode={countryCode}
          setCountryCode={setCountryCode}
          areaCode={areaCode}
          setAreaCode={setAreaCode}
          generateTollFree={generateTollFree}
          setGenerateTollFree={setGenerateTollFree}
          numberOptions={numberOptions}
          setNumberOptions={setNumberOptions}
        />
      )}
      {showDetailsModal && (
        <TrackingNumberDetailsModal
          fallback={fallback}
          setFallback={setFallback}
          selectedFallbackUser={selectedFallbackUser}
          setSelectedFallbackUser={setSelectedFallbackUser}
          isNewNumber={isNewNumber}
          showDetailsModal={showDetailsModal}
          inboundRecording={inboundRecording}
          setInboundRecording={setInboundRecording}
          setInboundNotificationMessageCondition={setInboundNotificationMessageCondition}
          inboundNotificationMessageCondition={inboundNotificationMessageCondition}
          onClose={onClose}
          trackingNumber={trackingNumber}
          sources={sources}
          agents={agents}
          admins={admins}
          beginDeletionConfirmation={beginDeletionConfirmation}
          onNumbersChanged={onNumbersChanged}
          routingMethod={routingMethod}
          setRoutingMethod={setRoutingMethod}
          selectedUsers={selectedUsers}
          setSelectedUsers={setSelectedUsers}
          selectedTextInboundUser={selectedTextInboundUser}
          setSelectedTextInboundUsers={setSelectedTextInboundUsers}
          noAnswerSetting={noAnswerSetting}
          setNoAnswerSetting={setNoAnswerSetting}
          voicemailGreetingChanged={voicemailGreetingChanged}
          setVoicemailGreetingChanged={setVoicemailGreetingChanged}
          voicemailGreetingFile={voicemailGreetingFile}
          setVoicemailGreetingFile={setVoicemailGreetingFile}
          voicemailGreetingFilename={voicemailGreetingFilename}
          setVoicemailGreetingFilename={setVoicemailGreetingFilename}
          createLead={createLead}
          setCreateLead={setCreateLead}
          handleToggle={handleToggle}
          isEnabledInboundTexts={isEnabledInboundTexts}
          name={name}
          setName={setName}
          source={source}
          setSource={setSource}
          chosenNumber={chosenNumber}
          purchaseNumber={purchaseNumber}
          backToNumberSelection={backToNumberSelection}
        />
      )}
      {showConfirmationModal && (
        <ConfirmTrackingNumber
          onClose={onClose}
          name={name}
          agents={selectedUsers}
          number={chosenNumber}
          routingMethod={routingMethod}
          sourceId={source}
          selectedTextInboundUser={selectedTextInboundUser}
          shouldCreateLeads={createLead}
          fallback={fallback}
          selectedFallbackUser={selectedFallbackUser}
          voicemailGreetingFile={voicemailGreetingFile}
          voicemailGreetingFilename={voicemailGreetingFilename}
          onConfirmBuy={onNumbersChanged}
          inboundRecording={inboundRecording}
          isEnabledInboundTexts={isEnabledInboundTexts}
          inboundNotificationMessageCondition={inboundNotificationMessageCondition}
        />
      )}
      {showDeleteConfirmationModal && (
        <ConfirmDeleteTrackingNumber
          trackingNumberId={trackingNumber.id}
          chooseNotToDelete={chooseNotToDelete}
          onClose={onClose}
          onConfirmDelete={onNumbersChanged}
        />
      )}
    </>
  );
};

TrackingNumberModal.propTypes = {
  agents: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  admins: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  accountOwnerId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  trackingNumber: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    createLead: PropTypes.bool.isRequired,
    source: PropTypes.string.isRequired,
    users: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    routingMethod: PropTypes.string.isRequired,
  }).isRequired,
  onNumbersChanged: PropTypes.func.isRequired,
  sources: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isNewNumber: PropTypes.bool.isRequired,
};

export default TrackingNumberModal;
